import { useEffect, useRef } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { TaskDetailsPanel } from '../listView/TaskDetailsPanel';
import { useProjectWorkspace } from '../projects/hooks';
import { canEditProject } from '../projects/types';
import { SalesTaskDialog } from '../salesTasks/SalesTaskDialog';
import { useSalesTasks } from '../salesTasks/queries';
import { usableRows } from '../salesTasks/salesTasksLens';
import { canManageSections } from '../sections/types';
import { useProjectTasks, useTask } from '../tasks/hooks';
import type { Task } from '../tasks/types';

/**
 * A task link clicked in a message, opened over the Messages page (P1,
 * kyu-20260930-1755-polish, design-p1.md). Nothing navigates, so the thread
 * and its scroll stay where they were.
 *
 * Nothing here is a new editor. A project task opens TaskDetailsPanel mounted
 * the way My Day mounts it; a deal task (project_id null, deal_id set, the
 * tasks_one_parent rule) opens SalesTaskDialog, the editor the Sales Tasks
 * page uses for it. Anything this cannot open (no row, refused by RLS, a room,
 * a failed read) calls onFallback, and the page follows the link exactly as
 * it did before.
 *
 * Every hook lives in this file, and the component only mounts after a link
 * is clicked, so the MessagesPage suites need no new mocks (traps.md, the
 * drop3cHooks and QueryClientProvider lines).
 */

/**
 * The A/B switch Kyu is deciding (msg-kyu-decision-p1-SENT-1859.txt).
 * A: true, a deal task opens the Sales Task dialog.
 * B: false, a deal task keeps today's link (a full page load).
 */
export const DEAL_TASKS_OPEN_IN_PLACE = true;

export type RecordDialogKind = 'loading' | 'project' | 'deal' | 'fallback';

/** Which editor a loaded task opens in. Pure, so the A/B rule has its own test. */
export function recordDialogFor(
  task: { data: Task | null | undefined; isPending: boolean; isError: boolean },
  dealTasksOpen: boolean,
): RecordDialogKind {
  if (task.isPending) {
    return 'loading';
  }
  // A failed background refetch keeps the row React Query already had
  // (isError with data): keep showing it, never navigate away mid-edit.
  const row = task.data;
  if (row === null || row === undefined) {
    return 'fallback';
  }
  if (row.project_id !== null) {
    return 'project';
  }
  if (row.deal_id !== null && dealTasksOpen) {
    return 'deal';
  }
  return 'fallback';
}

/** Calls onFallback once, the first render the condition holds. */
function useFallbackWhen(condition: boolean, onFallback: () => void) {
  const done = useRef(false);
  useEffect(() => {
    if (condition && !done.current) {
      done.current = true;
      onFallback();
    }
  }, [condition, onFallback]);
}

export function MessageRecordDialog({
  taskId,
  onClose,
  onFallback,
}: {
  taskId: string;
  /** Must be referentially stable: TaskDetailsPanel's Drawer depends on it. */
  onClose: () => void;
  /** The record cannot open here: the page follows the link instead. */
  onFallback: () => void;
}) {
  const task = useTask(taskId);
  const kind = recordDialogFor(task, DEAL_TASKS_OPEN_IN_PLACE);
  useFallbackWhen(kind === 'fallback', onFallback);

  if (kind === 'project' && task.data) {
    return <ProjectTask row={task.data} onClose={onClose} onFallback={onFallback} />;
  }
  if (kind === 'deal') {
    return <DealTask taskId={taskId} onClose={onClose} onFallback={onFallback} />;
  }
  return null;
}

/** As MyDayPage mounts it, with ProjectPage's own edit rights. */
function ProjectTask({
  row,
  onClose,
  onFallback,
}: {
  row: Task;
  onClose: () => void;
  onFallback: () => void;
}) {
  const { access } = useAuth();
  const projectId = row.project_id ?? undefined;
  const workspace = useProjectWorkspace(projectId);
  const projectTasks = useProjectTasks(projectId);
  const pending = workspace.isPending || projectTasks.isPending;
  const project = workspace.data?.project;
  // A room holds messages, not tasks (ProjectPage sends a room to Messages).
  // Only a first load with no data falls back; a failed refetch keeps the
  // data it had, so the open drawer stays.
  const unusable = !pending && (!workspace.data || !projectTasks.data || project?.is_room === true);
  useFallbackWhen(unusable, onFallback);

  if (pending || unusable || !workspace.data || !project || !projectTasks.data) {
    return null;
  }
  const profile = access.status === 'approved' ? access.profile : null;
  const { members, statuses } = workspace.data;
  // Read from the list so every save re-renders; a subtask is not in the
  // grouped list (groupTasks), so it opens from its own row.
  const shown = projectTasks.data.tasks.find((candidate) => candidate.id === row.id) ?? row;

  return (
    <TaskDetailsPanel
      key={shown.id}
      task={shown}
      subtasks={projectTasks.data.subtasksByParent[shown.id] ?? []}
      lookups={{ members, statuses }}
      projectId={project.id}
      projectName={project.name}
      canEdit={profile ? canEditProject(profile, project.owner_id, members) : false}
      canManage={profile ? canManageSections(profile, project.owner_id) : false}
      // No row here carries the field editors, the same case as My Day.
      fieldsEditable
      projectVisibility={project.visibility}
      ensureAssignable={(_userId, then) => then()}
      onClose={onClose}
    />
  );
}

/** The Sales Tasks page's own read and dialog (SalesTasksPage.tsx). */
function DealTask({
  taskId,
  onClose,
  onFallback,
}: {
  taskId: string;
  onClose: () => void;
  onFallback: () => void;
}) {
  const tasks = useSalesTasks();
  const row = tasks.data
    ? (usableRows(tasks.data).find((each) => each.id === taskId) ?? null)
    : null;
  const shownOnce = useRef(false);
  if (row !== null) {
    shownOnce.current = true;
  }
  // Missing on the first load: follow the link. Gone after a save (the list
  // refetches): close instead, never navigate away mid-edit.
  const missing = !tasks.isPending && row === null;
  useFallbackWhen(missing && !shownOnce.current, onFallback);
  useEffect(() => {
    if (missing && shownOnce.current) {
      onClose();
    }
  }, [missing, onClose]);

  return row === null ? null : <SalesTaskDialog key={row.id} task={row} onClose={onClose} />;
}
